import React, { useState, useEffect, useMemo } from "react";
import ChartVisualization from "../../../commonUI/Charts/ChartVisualization";
import TooltipIcon from "../../../commonUI/Tooltip/TooltipIcon";
import KPICardsGrid from "../../../commonUI/Cards/KPICardsGrid";
import {
  BarGraphIcon1,
  BarGraphIcon2,
  BarGraphIcon3,
} from "../../../commonUI/Icons/SvgIcon";
import UserActivityTable from "./UserActivityTable";

const INTERVAL_OPTIONS = ["Daily", "Weekly", "Monthly", "Yearly"];

const PERIOD_TO_INTERVAL = {
  "7D": "Daily",
  "30D": "Weekly",
  "3M": "Monthly",
  "6M": "Monthly",
  "12M": "Monthly",
};

const tooltipContent = {
  jobsPosted: "Number of jobs posted in the selected period.",
  bookingsMade:
    "Bookings made in the selected period, regardless of when the job was posted.",
  jobsCompleted: "Jobs completed in the selected period.",
};

const fmtDateShort = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      })
    : "";

const fmtDateByInterval = (iso, interval, useMonthOnly = false) => {
  if (!iso) return "";
  const date = new Date(iso);

  switch (interval) {
    case "Weekly":
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}-${weekEnd.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}`;
    case "Monthly":
      return date.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    case "Yearly":
      return date.getFullYear().toString();
    default: // Daily
      if (useMonthOnly) {
        return date.toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        });
      }
      return fmtDateShort(iso);
  }
};

const getDateRangeLabel = (data, interval) => {
  if (!data || data.length === 0) return "";

  const firstDate = new Date(data[0].date);
  const lastDate = new Date(data[data.length - 1].date);

  const formatDate = (date) =>
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

  return `${formatDate(firstDate)} to ${formatDate(lastDate)}`;
};

const aggregateDataByInterval = (data, interval) => {
  if (!data || data.length === 0) return data;

  if (interval === "Daily" || data.length <= 7) return data;

  if (interval === "Weekly") {
    const grouped = {};
    data.forEach((item) => {
      const date = new Date(item.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split("T")[0];

      if (!grouped[weekKey]) {
        grouped[weekKey] = {
          date: weekKey,
          month: `${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}-${new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}`,
          total: 0,
          client: 0,
          driver: 0,
          count: 0,
        };
      }
      grouped[weekKey].total += item.total;
      grouped[weekKey].client += item.client;
      grouped[weekKey].driver += item.driver;
      grouped[weekKey].count += 1;
    });

    return Object.values(grouped).map((item) => ({
      ...item,
      total: Math.round(item.total / item.count),
      client: Math.round(item.client / item.count),
      driver: Math.round(item.driver / item.count),
    }));
  }

  if (interval === "Monthly") {
    const grouped = {};
    data.forEach((item) => {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`,
          month: date.toLocaleDateString("en-GB", {
            month: "short",
            year: "numeric",
            timeZone: "UTC",
          }),
          total: 0,
          client: 0,
          driver: 0,
          count: 0,
        };
      }
      grouped[monthKey].total += item.total;
      grouped[monthKey].client += item.client;
      grouped[monthKey].driver += item.driver;
      grouped[monthKey].count += 1;
    });

    return Object.values(grouped).map((item) => ({
      ...item,
      total: Math.round(item.total / item.count),
      client: Math.round(item.client / item.count),
      driver: Math.round(item.driver / item.count),
    }));
  }

  return data;
};

const OverviewSection = ({ snapshot, selectedPeriod }) => {
  const [chartType, setChartType] = useState("line");
  const [selectedMetric, setSelectedMetric] = useState("all");
  const [selectedInterval, setSelectedInterval] = useState(
    PERIOD_TO_INTERVAL[selectedPeriod] || "Daily",
  );

  useEffect(() => {
    const mapped = PERIOD_TO_INTERVAL[selectedPeriod];
    if (mapped) setSelectedInterval(mapped);
  }, [selectedPeriod]);

  const ts = snapshot?.metrics?.timeseries || [];
  const platforms = (snapshot?.metrics?.platforms || [])
    .slice()
    .sort((a, b) => b.activeUsers - a.activeUsers);

  // Generate date points for the selected period so X-axis labels are correct
  const rawChartData = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const mo = today.getMonth(); // 0-based
    const d = today.getDate();

    const dayISO = (offset) =>
      new Date(y, mo, d - offset).toISOString().split("T")[0];

    const monthISO = (offset) =>
      new Date(y, mo - offset, 1).toISOString().split("T")[0];

    const fakeRow = (dateStr) => ({
      month: "",
      date: dateStr,
      total: Math.floor(Math.random() * 100) + 50,
      client: Math.floor(Math.random() * 50) + 20,
      driver: Math.floor(Math.random() * 40) + 15,
    });

    if (selectedPeriod === "7D")
      return Array.from({ length: 7 }, (_, i) => fakeRow(dayISO(6 - i)));
    if (selectedPeriod === "30D")
      return Array.from({ length: 30 }, (_, i) => fakeRow(dayISO(29 - i)));
    if (selectedPeriod === "3M")
      return Array.from({ length: 3 }, (_, i) => fakeRow(monthISO(2 - i)));
    if (selectedPeriod === "6M")
      return Array.from({ length: 6 }, (_, i) => fakeRow(monthISO(5 - i)));
    if (selectedPeriod === "12M")
      return Array.from({ length: 13 }, (_, i) => fakeRow(monthISO(12 - i)));

    // fallback: use ts timeseries data
    return ts.map((row) => ({
      month: "",
      date: row.bucket,
      total: Math.floor(Math.random() * 100) + 50,
      client: Math.floor(Math.random() * 50) + 20,
      driver: Math.floor(Math.random() * 40) + 15,
      activeUsers: row.activeUsers,
      sessions: row.sessions,
    }));
  }, [selectedPeriod]); // re-generate only when period changes

  // Aggregate data based on user's selected interval
  const aggregatedData = aggregateDataByInterval(
    rawChartData,
    selectedInterval,
  );

  // Format month labels based on selected interval
  const chartData = aggregatedData.map((row) => ({
    ...row,
    month: row.month || fmtDateByInterval(row.date, selectedInterval),
  }));

  const kpiData = [
    {
      label: "Jobs posted",
      tooltipTitle: tooltipContent.jobsPosted,
      value: "2,150",
      changePercent: "-8.6%",
      changeType: "negative",
    },
    {
      label: "Bookings made",
      tooltipTitle: tooltipContent.bookingsMade,
      value: "735",
      changePercent: "+10.7%",
      changeType: "positive",
    },
    {
      label: "Jobs completed",
      tooltipTitle: tooltipContent.jobsCompleted,
      value: "634",
      changePercent: "+7.6%",
      changeType: "positive",
    },
  ];

  return (
    <>
      {/* KPI Cards */}
      <KPICardsGrid cards={kpiData} />

      {/* Business Activity Chart */}
      <div className="analytics-chart-box">
        <div className="analytics-chart-header">
          <div className="analytics-chart-title-header">
            <div className="analytics-chart-title-left">
              <div>
                <span className="analytics-chart-title">Business activity</span>
              </div>
              <TooltipIcon
                title="Overview of jobs posted, bookings made, and jobs completed over time."
                iconWidth="16"
                iconHeight="16"
                inline={true}
              />
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="analytics-metric-select analytics-metric-select--sm"
              >
                <option value="all">All metrics</option>
                <option value="total">Jobs Posted</option>
                <option value="client">Bookings Module</option>
                <option value="driver">Jobs Completed</option>
              </select>
            </div>
            <div className="analytics-chart-title-right">
              <select
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
                className="analytics-interval-select"
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <div className="analytics-chart-type-buttons">
                <button
                  onClick={() => setChartType("bar")}
                  className={`analytics-chart-type-btn ${chartType === "bar" ? "active" : ""}`}
                  title="Bar Chart"
                >
                  <BarGraphIcon1 />
                </button>
                <button
                  onClick={() => setChartType("area")}
                  className={`analytics-chart-type-btn ${chartType === "area" ? "active" : ""}`}
                  title="Area Chart"
                >
                  <BarGraphIcon2 />
                </button>
                <button
                  onClick={() => setChartType("line")}
                  className={`analytics-chart-type-btn ${chartType === "line" ? "active" : ""}`}
                  title="Line Chart"
                >
                  <BarGraphIcon3 />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="analytics-chart-content">
          <ChartVisualization
            data={chartData}
            chartType={`${chartType}-${selectedMetric}`}
            interval={selectedInterval}
          />
        </div>
      </div>

      {/* User Activity Section */}
      <UserActivityTable platforms={platforms} />
    </>
  );
};

export default OverviewSection;
